/**
 * Composable Workflows API client (DEV-732).
 *
 * The deterministic, frontend-driven flow: the module page calls these to drive
 * substage transitions. Interview answers still POST directly to the existing
 * documents `record-answer` route (via QuestionCard); document extraction uses
 * the existing `extract-answers` route.
 */
import { api } from './api'
import { tokenManager } from '../lib/api'
import type { InterviewQuestionPayload } from '../types/interview_question'

const API_URL = import.meta.env.VITE_API_URL || ''

export type Substage = 'gathering_info' | 'ai_processing' | 'review_edit' | 'completed'
export type NodeStatus =
  | 'untouched' | 'in_progress' | 'agent_working' | 'human_review'
  | 'completed' | 'needs_attention'

export interface WorkflowOutput {
  id: number
  kind: string
  program_document: string | null
  scorecard: number | null
  solution_taxonomy: string | null
  tagged_data: Record<string, unknown>
  output_version: number
  is_current: boolean
  label: string
  download_url: string | null
}

export interface WorkflowStepNode {
  id: number
  step_key: string
  name: string
  description: string
  module_key: string
  icon: string
  substage: Substage
  status: NodeStatus
  needs_attention_reason: string
  output_version: number
  entered_at: string | null
  confirmed_at: string | null
  interview: string | null
  ui_x: number
  ui_y: number
  step_index: number
  has_interview: boolean
  has_ai_processing: boolean
  has_review: boolean
  review_surface: string
  /** Keep the review surface mounted during `ai_processing` instead of showing
   *  the centred progress card (`ModuleAdapter.review_during_processing`). */
  review_during_processing?: boolean
  /** Whether the review stage offers the generic step-level "Re-run" controls,
   *  or the module re-runs through its own surface
   *  (`ModuleAdapter.supports_step_rerun`). Defaults to true server-side. */
  supports_step_rerun?: boolean
  outputs: WorkflowOutput[]
}

export interface WorkflowEdge {
  id: number
  from: string
  to: string
  kind: string
}

export interface WorkflowGraph {
  workflow: { key: string; name: string; version: number } | null
  nodes: WorkflowStepNode[]
  edges: WorkflowEdge[]
}

export interface ProcessingStatus {
  state: 'idle' | 'running' | 'completed' | 'failed'
  progress: number
  label: string
}

export interface InterviewProgress {
  answered: number
  total: number
  remaining: { key: string; label: string }[]
}

export interface WorkflowStepDetail extends WorkflowStepNode {
  processing?: ProcessingStatus
  next_question?: InterviewQuestionPayload | null
  interview_complete?: boolean
  interview_progress?: InterviewProgress | null
  /** Set when an interviewing step has no interview (its template didn't resolve):
   *  why there are no questions, instead of an endless "Loading questions…". */
  interview_error?: string
  review_context?: Record<string, any>
  /** Solution Generation only: how families are produced (step config; default 'ask'). */
  generation_mode?: 'ask' | 'ai' | 'manual'
  /** review_edit only: why this step can't be marked complete yet, as sentences
   *  to render. Mirrors the server's confirm guard — `confirm/` enforces it, so
   *  an empty array here is a hint that the click will succeed, not a promise. */
  confirm_blockers?: string[]
}

export interface DownstreamChoice {
  step_id: number
  step_key: string
  name: string
}

export interface WorkflowSummary {
  key: string
  name: string
  description: string
  first_step_key: string | null
  first_module_icon: string
  step_names: string[]
}

export interface RecentProgram {
  id: number
  name: string
  updated_at: string
  workflow: string | null
  current_step: { step_key: string; name: string; status: NodeStatus } | null
}

export interface ContextDocument {
  id: number
  name: string
  uploaded_at: string
  download_url: string | null
  fact_count: number
  extraction: { state: 'running' | 'completed' | 'failed'; answered_count?: number; label?: string } | null
  job_id?: string
  /** The chat upload this doc was promoted from (AgentUpload id), if any. */
  source_upload_id?: string | null
  /** True when the doc came from the agent chat (an upload or the conversation notes). */
  from_chat?: boolean
  content_length?: number
}

// ── Authoring (react-flow-style designer) ───────────────────────────────────

/** One designer-facing config field for a module (drives the structured editor). */
export interface ModuleConfigField {
  key: string
  label: string
  type: 'document_template' | 'select' | 'text' | 'toggle'
  required?: boolean
  help?: string
  options?: { value: string; label: string }[]
}

export interface ModulePaletteItem {
  key: string
  name: string
  description: string
  icon: string
  input_kinds: string[]
  output_kinds: string[]
  has_interview: boolean
  has_ai_processing: boolean
  has_review: boolean
  is_active: boolean
  config_schema: ModuleConfigField[]
}

/** A step as returned nested on the authoring workflow (module by key). */
export interface AuthoringStepRead {
  id: number
  step_key: string
  name: string
  description: string
  module: string
  config: Record<string, unknown>
  agent_instructions: string
  ui_x: number
  ui_y: number
  step_index: number
  /** Programs that have minted this step — deleting it removes it from them. */
  program_count: number
}

export interface AuthoringEdge {
  id: number
  workflow: number
  from_step: number
  to_step: number
  kind: string
  produces_kind: string
  consumes_kind: string
}

export interface AuthoringWorkflow {
  id: number
  key: string
  name: string
  description: string
  version: number
  is_published: boolean
  owner_level: 'system' | 'office'
  office: number | null
  office_name: string | null
  cloned_from: number | null
  steps: AuthoringStepRead[]
  edges: AuthoringEdge[]
}

export interface GraphValidation {
  valid: boolean
  errors: string[]
  warnings: string[]
}

export const workflowsApi = {
  listWorkflows: () =>
    api.get<{ workflows: WorkflowSummary[] }>(`/api/workflows/`),

  // ── authoring ─────────────────────────────────────────────────────────────
  // NOTE: api.get already unwraps DRF paginated responses to the `results`
  // array, so these must NOT read `.results` again (that yields undefined).
  listModules: (): Promise<ModulePaletteItem[]> =>
    api.get<ModulePaletteItem[]>(`/api/workflows/modules/`),

  listAuthoringWorkflows: (): Promise<AuthoringWorkflow[]> =>
    api.get<AuthoringWorkflow[]>(`/api/workflows/authoring/workflows/`),

  getAuthoringWorkflow: (id: number | string) =>
    api.get<AuthoringWorkflow>(`/api/workflows/authoring/workflows/${id}/`),

  createWorkflow: (body: { key: string; name: string; description?: string }) =>
    api.post<AuthoringWorkflow>(`/api/workflows/authoring/workflows/`, body),

  cloneWorkflowToOffice: (id: number | string) =>
    api.post<AuthoringWorkflow>(`/api/workflows/authoring/workflows/${id}/clone-to-office/`),

  updateWorkflow: (id: number | string, body: Partial<AuthoringWorkflow>) =>
    api.patch<AuthoringWorkflow>(`/api/workflows/authoring/workflows/${id}/`, body),

  validateWorkflow: (id: number | string) =>
    api.get<GraphValidation>(`/api/workflows/authoring/workflows/${id}/validate/`),

  createStep: (body: {
    workflow: number; module: string; step_key: string; name: string
    ui_x?: number; ui_y?: number; step_index?: number; config?: Record<string, unknown>
  }) => api.post<AuthoringStepRead>(`/api/workflows/authoring/steps/`, body),

  updateStep: (id: number, body: Partial<AuthoringStepRead & { module: string }>) =>
    api.patch<AuthoringStepRead>(`/api/workflows/authoring/steps/${id}/`, body),

  deleteStep: (id: number) => api.delete(`/api/workflows/authoring/steps/${id}/`),

  createEdge: (body: {
    workflow: number; from_step: number; to_step: number; kind?: string
  }) => api.post<AuthoringEdge>(`/api/workflows/authoring/edges/`, body),

  deleteEdge: (id: number) => api.delete(`/api/workflows/authoring/edges/${id}/`),

  recentPrograms: (limit = 8) =>
    api.get<{ programs: RecentProgram[] }>(`/api/workflows/recent-programs/?limit=${limit}`),

  listContextDocuments: (programId: number | string) =>
    api.get<{ documents: ContextDocument[] }>(`/api/workflows/programs/${programId}/context-documents/`),

  uploadContextDocument: async (programId: number | string, file: File): Promise<ContextDocument> => {
    const fd = new FormData()
    fd.append('file', file)
    const resp = await fetch(`${API_URL}/api/workflows/programs/${programId}/context-documents/`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
      body: fd,
    })
    if (!resp.ok) throw new Error((await resp.json().catch(() => ({}))).error || 'Upload failed')
    return resp.json()
  },

  deleteContextDocument: (programId: number | string, docId: number) =>
    api.delete<{ deleted_facts: number }>(`/api/workflows/programs/${programId}/context-documents/${docId}/`),

  getGraph: (programId: number | string) =>
    api.get<WorkflowGraph>(`/api/workflows/programs/${programId}/graph/`),

  mint: (programId: number | string) =>
    api.post<WorkflowGraph>(`/api/workflows/programs/${programId}/mint/`),

  getStep: (stepId: number | string) =>
    api.get<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/`),

  startStep: (stepId: number | string) =>
    api.post<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/start/`),

  nextQuestion: (stepId: number | string) =>
    api.get<{ next_question: InterviewQuestionPayload | null; interview_complete: boolean; interview_id: string | null }>(
      `/api/workflows/steps/${stepId}/next-question/`,
    ),

  processStep: (stepId: number | string, feedback?: string) =>
    api.post<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/process/`,
      feedback ? { feedback } : {}),

  /** Skip AI processing and fill the artifact by hand (Solution Generation: tech-tree picks). */
  startManual: (stepId: number | string) =>
    api.post<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/start-manual/`),

  confirmStep: (stepId: number | string) =>
    api.post<{ step: WorkflowStepNode; downstream: DownstreamChoice[] }>(
      `/api/workflows/steps/${stepId}/confirm/`,
    ),

  reopenStep: (stepId: number | string) =>
    api.post<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/reopen/`),

  /** Recompute a step's review artifact in place (contract selection, after an answer). */
  refreshStep: (stepId: number | string) =>
    api.post<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/refresh/`),

  /** Contract Selection: save the picked {type, vehicle} pairs (DEV-735/736). */
  saveSelection: (stepId: number | string, selected: ContractSelectionPick[]) =>
    api.post<WorkflowStepDetail>(`/api/workflows/steps/${stepId}/selection/`, { selected }),
}

// ── Contract Selection report (apps/contracts recommend_type_led) ──────────

export interface ContractQuestion {
  spec_key: string
  spec_id: string
  spec_type: string
  options: { value: string; label: string }[]
  question: string
  rule_group: string | null
  kind: 'legal' | 'preference'
}

export interface ContractSurveyQuestion extends ContractQuestion {
  affects: { contract_type_id: string; name: string }[]
}

/** A rule we couldn't check, stated as what must hold. An any_of group is one
 *  condition with alternatives. */
export interface ContractCondition {
  condition: string
  spec_keys: string[]
  any_of: string[] | null
}

export interface ContractCitation {
  spec_key: string
  condition?: string
  rationale: string
  sources: string[]
}

export interface ContractConfidence {
  level: 'high' | 'medium' | 'low'
  reason: string
}

export interface ContractVehicleItem {
  vehicle_id: string
  name: string
  short_name: string
  vehicle_type_label: string
  url: string
  scope_summary: string
  decision: string
  matched_families: { family_id: string; family_name: string; technology_name: string; via_technology_name: string }[]
  match_score: number
  mapped_technology_count: number
  questions_to_ask: ContractQuestion[]
  blocking_questions: ContractQuestion[]
  last_verified: string | null
  confidence: ContractConfidence
  unchecked_conditions: ContractCondition[]
}

export interface ContractTypeItem {
  rank: number | null
  contract_type_id: string
  name: string
  short_name: string
  family: string
  authority: string
  description: string
  is_vehicle_based: boolean
  decision: 'eligible' | 'insufficient_data'
  gates_passed: (ContractCitation & { rule_group: string | null })[]
  preferences_matched: (ContractCitation & { spec_name: string; value: string; value_label: string })[]
  preference_count: number
  duration: { typical_months: number; min_months: number | null; max_months: number | null; note: string } | null
  confidence: ContractConfidence
  questions_to_ask: ContractQuestion[]
  blocking_questions: ContractQuestion[]
  unchecked_conditions: ContractCondition[]
  last_verified: string | null
  vehicles: ContractVehicleItem[]
}

export interface ContractReport {
  types: ContractTypeItem[]
  ruled_out: { contract_type_id: string; name: string; authority: string; failed: ContractCitation[] }[]
  is_ranked: boolean
  notes: string[]
  families: { family_id: string; family_name: string; technology_name: string }[]
  survey: ContractSurveyQuestion[]
  known_facts: { spec_key: string; spec_name: string; value_label: string; source: string }[]
  generated_at?: string
}

export interface ContractSelectionPick {
  contract_type_id: string
  vehicle_id: string | null
}

/** Outline colour per node status (semantic tokens; matches the PRD palette). */
export function statusOutlineClass(status: NodeStatus): string {
  switch (status) {
    case 'agent_working':
      return 'border-green-500'
    case 'human_review':
      return 'border-amber-500'
    case 'completed':
      return 'border-foreground'
    case 'needs_attention':
      return 'border-destructive'
    case 'in_progress':
      return 'border-primary'
    default:
      return 'border-border'
  }
}

/**
 * Fill colour for a small status dot (sidebar step list). Mirrors the palette
 * in `statusOutlineClass`; the two "active" states (blue in-progress, green
 * agent-working) pulse so live work reads at a glance.
 */
export function statusDotClass(status: NodeStatus): string {
  switch (status) {
    case 'agent_working':
      return 'bg-green-500 animate-pulse'
    case 'in_progress':
      return 'bg-primary animate-pulse'
    case 'human_review':
      return 'bg-amber-500'
    case 'completed':
      return 'bg-foreground'
    case 'needs_attention':
      return 'bg-destructive'
    default:
      return 'bg-muted-foreground/40'
  }
}

export function statusLabel(status: NodeStatus): string {
  switch (status) {
    case 'agent_working': return 'AI working'
    case 'human_review': return 'Review'
    case 'completed': return 'Complete'
    case 'needs_attention': return 'Needs attention'
    case 'in_progress': return 'In progress'
    default: return 'Not started'
  }
}
